<template>
  <div class="view-content creator-view">
    <h1 class="sr-only">Angiv mål på din szafa</h1>
    <div
      v-if="!loaded || isStepChangeInProgress"
      class="loading-overlay">
      <div class="loader">
        <span>Indlæsning af data&hellip;</span>
      </div>
    </div>

    <wardrobe-preview
      :current-wardrobe-height="data.height"
      :current-wardrobe-width="data.width" />

    <right-sidebar
      :should-send-doors="false"
      :should-send-inventory="false"
      :view-local-state="localState">
      <template v-slot:header>
        <h2>Angiv mål</h2>
      </template>

      <template v-slot:content>
        <validation-observer
          ref="dimensions-form"
          tag="form"
          id="dimensionsForm"
          class="new-radio-modificate-form dimensions-form-modificate">

          <div class="form-field">
            <validation-provider
              :rules="`required|min_value:${wardrobeMinWidth}|max_value:${wardrobeMaxWidth}`"
              name="wardrobe-width"
              v-slot="{ errors }">
              <label
                for="wardrobe-width"
                :class="{
                  'form-input': true,
                  'is-invalid': errors.length
                }">
                <span class="form-input-name">Bredde</span>
                <c-input
                  type="number"
                  v-model="data.width"
                  :state="errors.length ? 'invalid' : null"/>
                <span class="form-input-appendix">mm</span>
              </label>
              <div
                :class="{
                  'field-instructions': true,
                  'text-danger': errors.length
                }">
                OBS! Bredden skal være imellem {{ wardrobeMinWidth }}&nbsp;mm og {{ wardrobeMaxWidth }}&nbsp;mm.
              </div>
            </validation-provider>
          </div>

          <div class="form-field">
            <validation-provider
              :rules="`required|min_value:${wardrobeMinHeight}|max_value:${wardrobeMaxHeight}`"
              name="wardrobe-height"
              v-slot="{ errors }">
              <label
                for="wardrobe-height"
                :class="{
                  'form-input': true,
                  'is-invalid': errors.length
                }">
                <span class="form-input-name">Højde</span>
                <c-input
                  type="number"
                  v-model="data.height"
                  :state="errors.length ? 'invalid' : null"/>
                <span class="form-input-appendix">mm</span>
              </label>

              <div
                :class="{
                  'field-instructions': true,
                  'text-danger': errors.length
                }">
                OBS! Højden skal være imellem {{ wardrobeMinHeight }}&nbsp;mm og {{ wardrobeMaxHeight }}&nbsp;mm.
              </div>
            </validation-provider>
          </div>

          <div
            v-if="onlyDoorConfig"
            class="showcase-attention text-center mt-5">
            <strong>Har du ujævne vægge?</strong>
            <p class="mb-1">Tilføj en anslag liste til din ordre. Et anslag monteres til væggen for rette skævheder op.</p>

            <div class="form-group">
              <validation-provider
                rules="required"
                name="has-uneven-walls"
                v-slot="{ errors }"
                tag="div"
                class="flex flex-column mx-auto">
                <div class="wardrobe-positions flex flex-wrap  new-style-radio">
                  <c-form-radio
                    :value="true"
                    v-model="data.useImpact"
                    :class="{ 'is-invalid': errors.length }">
                    <span class="c-form-radio-label bold">Tilføj anslag</span>
                  </c-form-radio>
                  <c-form-radio
                    :value="false"
                    v-model="data.useImpact"
                    :class="{ 'is-invalid': errors.length }">
                    <span class="c-form-radio-label bold">Nej tak til anslag</span>
                  </c-form-radio>
                </div>
              </validation-provider>
            </div>
          </div>
        </validation-observer>

        <div class="text-center mt-3 showcase-attention">
          <strong>Se vores opmåling guide her</strong>
          <p>Husk at det er vigtigt at måle højde i 3 forskellige placeringer i forhold til loftet. Man skal angive det laveste mål i programmet for at få gavle tilpasset. Husk ved at vælge “væg til væg” placering, beregner vi 10&nbsp;mm luft mellem væggene.</p>
        </div>

        <div
          class="showcase-data"
          style="cursor: pointer;"
          @click.prevent="showMeasurementModal">
          <strong class="showcase-data-title text-center">Hvordan måler jeg rigtigt op?</strong>
          <measurement-instructions/>
        </div>
      </template>

      <template v-slot:footer>
        <navigation-buttons
          :form-id="'dimensionsForm'"
          :is-go-back-disabled="getIsWardrobeCreatorModeDisabled"
          @nextBtnClicked="validateForm"
          @backBtnClicked="goBack" />
      </template>
    </right-sidebar>

    <transition name="fade">
      <c-modal
        v-if="isMeasurementModalVisible"
        ref="measurement-modal"
        :class="{ 'is-visible': isMeasurementModalVisible, 'is-narrow is-centered': true }"
        :isPersistent="false"
        :is-visible="isMeasurementModalVisible"
        @close="handleMeasurementModalClose">
        <c-modal-header class="text-center">
          Hvordan måler jeg rigtigt op?
        </c-modal-header>
        <c-modal-body>
          <measurement-instructions/>
        </c-modal-body>
      </c-modal>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import FormUtils from '@/utils/FormUtils.js';
import StateUtils from '@/utils/StateUtils.js';
import MeasurementInstructions from '@/components/forms/dimensions/MeasurementInstructions.vue';

export default {
  name: 'wardrobe-dimensions',
  components: {
    'measurement-instructions': MeasurementInstructions
  },
  computed: {
    ...mapGetters('application', [
      'wardrobeMaxWidth',
      'wardrobeMaxHeight',
      'wardrobeMinHeight',
      'wardrobeMinWidth'
    ]),
    ...mapGetters('creator', [
      'wardrobeHeight',
      'wardrobeWidth',
      'wardrobeLeftStrokeAdjustment',
      'wardrobeRightStrokeAdjustment',
      'hasDoor',
      'hasInventory',
      'getIsWardrobeCreatorModeDisabled'
    ]),
    onlyDoorConfig () {
      return this.hasDoor && !this.hasInventory;
    },
    localState () {
      let leftStrokeAdjustment = this.onlyDoorConfig ? this.data.useImpact : this.wardrobeLeftStrokeAdjustment;
      let rightStrokeAdjustment = this.onlyDoorConfig ? this.data.useImpact : this.wardrobeRightStrokeAdjustment;

      let output = {
        wardrobe: {
          height: this.data.height,
          impact: this.data.useImpact,
          left_stroke_adjustment: leftStrokeAdjustment,
          right_stroke_adjustment: rightStrokeAdjustment,
          width: this.data.width
        }
      };
      return output
    }
  },
  data () {
    return {
      data: {
        height: 0,
        useImpact: true,
        width: 0
      },
      isMeasurementModalVisible: false,
      isStepChangeInProgress: false,
      loaded: false
    }
  },
  mounted () {
    this.loadDataFromStore();
  },
  methods: {
    loadDataFromStore () {
      Vue.set(this, 'loaded', false);
      this.data.height = this.$store.getters['creator/wardrobeHeight'] || 0;
      this.data.width = this.$store.getters['creator/wardrobeWidth'] || 0;
      this.data.useImpact = this.$store.getters['creator/wardrobeImpact'] || false;

      if (!this.data.height) {
        this.data.height = this.wardrobeMinHeight;
      }

      if (!this.data.width) {
        this.data.width = this.wardrobeMinWidth;
      }

      Vue.nextTick(() => {
        this.loaded = true;
      });
    },
    validateForm () {
      Vue.set(this, 'isStepChangeInProgress', true);
      FormUtils.validate(this.$refs['dimensions-form'], this.confirmSubmitForm, this.handleStepChangeStop);
    },
    handleStepChangeStop () {
      Vue.set(this, 'isStepChangeInProgress', false);
    },
    async confirmSubmitForm () {
      if (this.$store.getters['creator/getIsStepCompleted']('wardrobeDimensions') && this.areChangesMade()) {
        Vue.swal({
          title: 'Note!',
          text: 'Ændringer i dette trin, kan have indflydelse på senere trin.',
          icon: 'warning',
          confirmButtonText: 'OK',
        }).then(async () => {
          await this.saveState(true);

          this.handleStepChangeStop();
          this.goNext();
        });
      } else {
        await this.saveState(true);

        this.handleStepChangeStop();
        this.goNext();
      }
    },
    async saveState (shouldMarkStepCompleted = false) {
      let isSettingChanged = this.areChangesMade();
      if (isSettingChanged) {
        this.$store.commit('creator/resetStepsAfter', 'wardrobePosition'); // reset after wardrobePosition because dimensions don't affect position
        this.$store.commit('creator/resetInventory');
      }
      this.$store.commit('creator/setWardrobeHeight', Number(this.data.height));
      this.$store.commit('creator/setWardrobeWidth', Number(this.data.width));

      if (this.onlyDoorConfig) {
        this.$store.commit('creator/setWardrobeUseImpact', this.data.useImpact);
        this.$store.commit('creator/setWardrobeLeftStrokeAdjustment', this.data.useImpact);
        this.$store.commit('creator/setWardrobeRightStrokeAdjustment', this.data.useImpact);
      }

      if (shouldMarkStepCompleted) {
        this.$store.commit('creator/setStepCompletion', { stepName: 'wardrobeDimensions', stepCompleted: true });
      }

      await this.$nextTick();
    },
    goNext () {
      if (!this.hasDoor && this.hasInventory) {
        this.$router.push({ name: 'wardrobeInventory' });
        return;
      }

      if (this.hasDoor && !this.hasInventory) {
        this.$router.push({ name: 'wardrobeDoorsNumber' });
        return;
      }

      this.$router.push({ name: 'wardrobePosition' });
    },
    goBack () {
      Vue.set(this, 'isStepChangeInProgress', true);
      this.saveState();

      Vue.nextTick(() => {
        this.handleStepChangeStop();
        this.$router.push({ name: 'startScreen' });
      });
    },
    areChangesMade () {
      return StateUtils.isDimensionsChanged(this.localState, this.$store.getters['creator/creatorWardrobeData']);
    },
    showLoadDataPopup (wardrobeSavedState) {
      Vue.swal({
        title: 'Gendan tidligere projekt',
        text: 'Der er et tidligere projekt tilgængeligt. Vil du indlæse den?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja',
        cancelButtonText: 'Nej',
        showCloseButton: false,
        showLoaderOnConfirm: true,
        allowOutsideClick: false
      }).then((result) => {
        if (result.value) {
          let creatorSteps = Object.keys(wardrobeSavedState.stepsCompletion);
          for (let i = 0; i < creatorSteps.length; i++) {
            this.$store.commit('creator/setStepCompletion', { stepName: creatorSteps[i], stepCompleted: wardrobeSavedState.stepsCompletion[creatorSteps[i]] });
          }

          this.$store.commit('creator/setDoors', wardrobeSavedState.doors);
          this.$store.commit('creator/setWardrobe', wardrobeSavedState.wardrobe);
          this.$store.commit('creator/setInventory', wardrobeSavedState.inventory);
        }

        Vue.nextTick(() => {
          this.loadDataFromStore();
        });
      })
    },
    showMeasurementModal () {
      this.isMeasurementModalVisible = true;
    },
    handleMeasurementModalClose () {
      this.isMeasurementModalVisible = false;
    }
  }
};
</script>
